/**
 * CNPJ alfanumérico (regra vigente desde 2026).
 *
 * As 12 primeiras posições (raiz + ordem) podem conter `[0-9A-Z]`; os 2 dígitos
 * verificadores são SEMPRE numéricos. O cálculo do DV usa o valor ASCII do
 * caractere menos 48 — `'0'` = 0 … `'9'` = 9, `'A'` = 17 … `'Z'` = 42 — de forma
 * que um CNPJ puramente numérico continua produzindo exatamente o mesmo DV do
 * algoritmo antigo.
 */

const COMPRIMENTO_CNPJ = 14;

/** Pesos do 1º dígito verificador (12 caracteres da base). */
const PESOS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

/** Pesos do 2º dígito verificador (12 caracteres da base + 1º DV). */
const PESOS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

/** Base alfanumérica nas 12 primeiras posições, DV sempre numérico. */
const FORMATO_CNPJ = /^[0-9A-Z]{12}[0-9]{2}$/;

/** Todos os 14 caracteres iguais (`00000000000000`, `88888888888888`, …). */
const TODOS_IGUAIS = /^(.)\1{13}$/;

const MASCARA_CNPJ = /^(.{2})(.{3})(.{3})(.{4})(.{2})$/;

/**
 * Remove qualquer caractere que não seja `[0-9A-Za-z]` e devolve em caixa alta.
 */
export function normalizarCnpj(valor: string): string {
  return valor.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** Valor numérico do caractere para o cálculo do DV: código ASCII − 48. */
function valorCaractere(caractere: string): number {
  return caractere.charCodeAt(0) - 48;
}

function calcularDigito(base: string, pesos: readonly number[]): number {
  let soma = 0;
  for (let indice = 0; indice < pesos.length; indice += 1) {
    soma += valorCaractere(base.charAt(indice)) * (pesos[indice] as number);
  }
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/**
 * Valida um CNPJ (numérico ou alfanumérico) pelos dígitos verificadores.
 *
 * Rejeita: comprimento diferente de 14, caractere fora de `[0-9A-Z]`, DV não
 * numérico, todos os caracteres iguais e ordem (posições 9–12) igual a `0000`.
 */
export function validarCnpj(valor: string): boolean {
  const cnpj = normalizarCnpj(valor);

  if (cnpj.length !== COMPRIMENTO_CNPJ) return false;
  if (!FORMATO_CNPJ.test(cnpj)) return false;
  if (TODOS_IGUAIS.test(cnpj)) return false;
  // Ordem (posições 9–12) `0000` não existe: o primeiro estabelecimento é `0001`.
  if (cnpj.slice(8, 12) === '0000') return false;

  const base = cnpj.slice(0, 12);
  const dv1 = calcularDigito(base, PESOS_DV1);
  const dv2 = calcularDigito(`${base}${dv1}`, PESOS_DV2);

  return cnpj.slice(12) === `${dv1}${dv2}`;
}

/**
 * Formata como `00.000.000/0000-00`.
 *
 * Se o valor normalizado não tiver 14 caracteres, devolve o valor normalizado
 * sem máscara (útil para exibir o que o usuário está digitando).
 */
export function formatarCnpj(valor: string): string {
  const cnpj = normalizarCnpj(valor);
  if (cnpj.length !== COMPRIMENTO_CNPJ) return cnpj;
  return cnpj.replace(MASCARA_CNPJ, '$1.$2.$3/$4-$5');
}
